import { Component, ReactNode } from "react";

// Recovers from Vite stale-chunk errors after a redeploy.
// "Failed to fetch dynamically imported module" means the browser is holding
// an old hashed URL that no longer exists. A one-time hard reload fixes it.
const RELOAD_FLAG = "decyzon:lazy-chunk-reloaded";

function isChunkLoadError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err ?? "");
  return (
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    /ChunkLoadError/i.test(msg) ||
    /Loading chunk [\w-]+ failed/i.test(msg)
  );
}

interface State {
  hasError: boolean;
}

export default class LazyChunkBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: unknown): State {
    if (isChunkLoadError(error)) {
      try {
        if (!sessionStorage.getItem(RELOAD_FLAG)) {
          sessionStorage.setItem(RELOAD_FLAG, "1");
          window.location.reload();
          return { hasError: true };
        }
      } catch {
        // sessionStorage unavailable — fall through to error UI
      }
      return { hasError: true };
    }
    throw error;
  }

  componentDidMount() {
    try {
      // Clear flag on a successful mount so future stale chunks can self-heal.
      if (!this.state.hasError) sessionStorage.removeItem(RELOAD_FLAG);
    } catch {
      /* noop */
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-[60vh] flex items-center justify-center px-6 text-center">
          <div className="max-w-sm space-y-3">
            <p className="text-sm text-muted-foreground">
              A new version is available. Refresh to continue.
            </p>
            <button
              onClick={() => {
                try { sessionStorage.removeItem(RELOAD_FLAG); } catch { /* noop */ }
                window.location.reload();
              }}
              className="text-sm font-medium underline underline-offset-4"
            >
              Refresh
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
